import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, NavLink } from 'react-router-dom';
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import '@aws-amplify/ui-react/styles.css';
import { Home, LayoutDashboard, FilePlus2, Settings, LogOut, LogIn, Sparkles, BarChart3, Monitor, GitBranch, ChevronLeft, ChevronRight } from 'lucide-react';
import testGeniusLogo from './TestGeniusLogo.png';
import FileList from './components/FileList';
import FileUpload from './components/FileUpload';
import FileReview from './components/FileReview';
import HomePage from './components/HomePage';
import SettingsPage from './components/SettingsPage';
import TestReport from './components/TestReport';
import Dashboards from './components/Dashboards';
import BugDashboard from './components/BugDashboard';
import AutomationDashboard from './components/AutomationDashboard';
import SmokeSanityDashboard from './components/SmokeSanityDashboard';
import BugHygieneDashboard from './components/BugHygieneDashboard';
import FleetManager from './components/FleetManager';
import Pipeline from './components/Pipeline';


function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signedOut, setSignedOut] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    checkUser();
    const hubListener = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') { setSignedOut(false); checkUser(); }
      if (payload.event === 'signInWithRedirect_failure') console.error('Sign in failed:', payload.data);
    });
    return () => hubListener();
  }, []);

  async function checkUser() {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      setUser({
        username: currentUser.username,
        email: session.tokens?.idToken?.payload?.email || currentUser.username
      });
      setSignedOut(false);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    setUser(null);
    setSignedOut(true);
  }

  function handleSignIn() {
    setLoading(true);
    signInWithRedirect({ provider: { custom: 'Federate' } });
  }

  if (loading) return <div className="loading">Authenticating...</div>;

  if (!user) {
    if (!signedOut) {
      // First load, not signed in — auto-redirect to Midway
      signInWithRedirect({ provider: { custom: 'Federate' } });
      return <div className="loading">Redirecting to Midway...</div>;
    }
    // User explicitly signed out — show landing page
    return (
      <div className="signed-out-page">
        <img src={testGeniusLogo} alt="TestGenius" style={{ width: 80, height: 80, marginBottom: 12 }} />
        <h1>TestGenius</h1>
        <p>You have been signed out successfully.</p>
        <button onClick={handleSignIn} className="btn btn-primary">
          <LogIn size={18} /> Sign in with Midway
        </button>
      </div>
    );
  }

  return (
    <div className="app-container">
      <aside className={`app-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <Link to="/" className="app-title">
          <Sparkles size={22} />
          {!sidebarCollapsed && <span>TestGenius</span>}
        </Link>
        <nav className="app-nav">
          <NavLink to="/" end className="nav-link" title="Home"><Home size={18} /> {!sidebarCollapsed && <span>Home</span>}</NavLink>
          <NavLink to="/files" className="nav-link" title="Review Dashboard"><LayoutDashboard size={18} /> {!sidebarCollapsed && <span>Review Dashboard</span>}</NavLink>
          <NavLink to="/upload" className="nav-link" title="New Review Request"><FilePlus2 size={18} /> {!sidebarCollapsed && <span>New Review Request</span>}</NavLink>
          <NavLink to="/dashboards" className="nav-link" title="Dashboards"><BarChart3 size={18} /> {!sidebarCollapsed && <span>Dashboards</span>}</NavLink>
          <NavLink to="/fleet-manager" className="nav-link" title="Fleet Manager"><Monitor size={18} /> {!sidebarCollapsed && <span>Fleet Manager</span>}</NavLink>
          <NavLink to="/pipeline" className="nav-link" title="Pipeline"><GitBranch size={18} /> {!sidebarCollapsed && <span>Pipeline</span>}</NavLink>
          <NavLink to="/settings" className="nav-link" title="Settings"><Settings size={18} /> {!sidebarCollapsed && <span>Settings</span>}</NavLink>
        </nav>
        <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
        <div className="user-info">
          {!sidebarCollapsed && <span>{user.email}</span>}
          <button onClick={handleSignOut} className="btn btn-secondary btn-sm"><LogOut size={14} /> {!sidebarCollapsed && 'Sign Out'}</button>
        </div>
      </aside>
      <main className={`app-main ${sidebarCollapsed ? 'expanded' : ''}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/files" element={<FileList />} />
          <Route path="/upload" element={<FileUpload />} />
          <Route path="/dashboards" element={<Dashboards />} />
          <Route path="/dashboards/test-report" element={<TestReport />} />
          <Route path="/dashboards/bug-dashboard" element={<BugDashboard />} />
          <Route path="/dashboards/automation" element={<AutomationDashboard />} />
          <Route path="/dashboards/smoke-sanity" element={<SmokeSanityDashboard />} />
          <Route path="/dashboards/bug-hygiene" element={<BugHygieneDashboard />} />
          <Route path="/fleet-manager" element={<FleetManager />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/review/:fileId" element={<FileReview />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;
